import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getRoomPublicUrl, Room, RoomVisibility } from "@/types/rooms";
import { Globe, Lock, Link as LinkIcon, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
interface RoomShareDialogProps {
  room: Room;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVisibilityChange: (visibility: RoomVisibility) => Promise<void> | void;
}

export function RoomShareDialog({
  room,
  open,
  onOpenChange,
  onVisibilityChange,
}: RoomShareDialogProps) {
  const { isRtl } = useTranslation();
  const [localVisibility, setLocalVisibility] =
    React.useState<RoomVisibility>(room.visibility);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    setLocalVisibility(room.visibility);
  }, [room.visibility]);

  const isPublic = localVisibility === "public";
  const publicUrl = getRoomPublicUrl(room);

  const handleSelect = async (visibility: RoomVisibility) => {
    if (visibility === localVisibility) return;
    setLocalVisibility(visibility);
    setCopied(false);
    await onVisibilityChange(visibility);
  };

  const handleCopy = async () => {
    if (!isPublic || !publicUrl) return;
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-xl p-4 pb-0" dir={isRtl ? "rtl" : "ltr"}>
      
        <DialogHeader>
          <DialogTitle className="text-base text-foreground/70">
            Invite your friends
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-4 pb-0">
          <div className="border border-border rounded-2xl overflow-hidden">
            {/* Private */}
            <button
              className={cn(
                "flex w-full items-center justify-between p-4 hover:bg-muted/50 transition-colors",
                localVisibility === "private" && "bg-muted/40"
              )}
              onClick={() => handleSelect("private")}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-full">
                  <Lock className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="text-left space-y-0.5">
                  <div className="text-sm font-medium text-foreground">Private</div>
                  <div className="text-xs text-muted-foreground">
                    Viewable by yourself only
                  </div>
                </div>
              </div>
              {localVisibility === "private" && (
                <Check className="w-5 h-5 text-primary" />
              )}
            </button>

            {/* Public */}
            <button
              className={cn(
                "flex w-full items-center justify-between p-4 border-t border-border hover:bg-muted/50 transition-colors",
                localVisibility === "public" && "bg-muted/40"
              )}
              onClick={() => handleSelect("public")}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-full">
                  <Globe className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="text-left space-y-0.5">
                  <div className="text-sm font-medium text-foreground">Public</div>
                  <div className="text-xs text-muted-foreground">
                    Anyone with link can view
                  </div>
                </div>
              </div>
              {localVisibility === "public" && (
                <Check className="w-5 h-5 text-primary" />
              )}
            </button>
          </div>
        </div>

        <div className="py-4 px-1">
          <button
            onClick={handleCopy}
            disabled={!isPublic}
            className={cn(
              "w-full h-10 rounded-lg text-sm font-medium flex items-center justify-center transition-colors",
              isPublic
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            )}
          >
            <LinkIcon className="h-4 w-4 mr-1.5" />
            {copied ? "Copied!" : "Copy Public Link"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
